import Router from 'next/router';
import { RefObject, useEffect } from 'react';

/**
 * Converts native <a> tags from external HTML to internal client-side navigation
 *
 * @see https://mtsknn.fi/blog/react-links-in-html/
 */
export const useInternalLinkClickHandlers = (ref: RefObject<HTMLDivElement>) => {
  useEffect(() => {
    if (!ref.current) return;

    const handleLinkClick = (event: MouseEvent) => {
      const link = event.currentTarget as HTMLAnchorElement;
      const href = link.getAttribute('href');
      const target = link.getAttribute('target');
      const url = new URL(href || '', window.location.origin);

      const isInternalLink = url.origin === window.location.origin;
      const isOpenedInSameWindow = !target || target === '_self';
      const isLeftButtonClick = event.button === 0;

      // E.g. Ctrl-clicking a link opens it in a new tab so let the browser handle such clicks
      const isModifierKeyPressed = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;

      if (isInternalLink && isOpenedInSameWindow && isLeftButtonClick && !isModifierKeyPressed) {
        event.preventDefault();
        Router.push(url.pathname + url.search + url.hash, undefined, { locale: false });
      }
    };

    const links = ref.current.querySelectorAll('a');

    links.forEach((link) => link.addEventListener('click', handleLinkClick));

    return () => {
      links.forEach((link) => link.removeEventListener('click', handleLinkClick));
    };
  }, [ref]);
};
